import { EllipsisVerticalIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo } from "react";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { toast } from "@/core/components/ui/toast";
import { useBusiness } from "@/core/business";
import { dayjs } from "@/core/utils/date";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATE } from "@/customer/constants";
import { useGetCustomer, useOptOutCustomer } from "@/customer/query";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { useGetCustomerTextMessages } from "@/text-messages/query";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";
import { TextMessageAttribution } from "./text-message-attribution";
import { Stack } from "./ui/stack";
import { Typography } from "./ui/typography";

interface DropdownAction {
  label: string;
  onClick: () => void;
  variant?: "default" | "destructive";
}

export const TextMessageSidebarHeader = ({
  selectedItem,
  setIsSidebarOpen,
}: {
  selectedItem: IMostRecentTextMessage | null;
  setIsSidebarOpen: (open: boolean) => void;
}) => {
  const { businessId } = useBusiness();
  const { data: customer, isLoading } = useGetCustomer(
    selectedItem?.customer.id,
  );
  const { data: messages } = useGetCustomerTextMessages(
    selectedItem?.customer.id,
  );
  const { mutate: optOutCustomer } = useOptOutCustomer(businessId);

  const oldestTextMessage = {
    ...[...(messages || [])].sort((a, b) =>
      dayjs(a.created_at).diff(dayjs(b.created_at)),
    )[0],
    customer: selectedItem?.customer,
  };
  const signupSource = customer?.signup_source;
  const customerToShow = getCustomerNameOrPhone(selectedItem?.customer);

  const handleOptOut = useCallback(() => {
    if (!customer?.id) return;

    optOutCustomer(customer.id, {
      onSuccess: () => {
        toast.success("Customer opted out from messaging");
        setIsSidebarOpen(false);
      },
      onError: () => {
        toast.error("Failed to opt out customer from messaging");
      },
    });
  }, [customer?.id, optOutCustomer, setIsSidebarOpen]);

  const showOptOut =
    customer?.id && customer.state !== CUSTOMER_STATE.opted_out;

  const dropdownActions = useMemo(
    () =>
      [
        showOptOut && {
          label: "Opt out from messaging",
          onClick: handleOptOut,
          variant: "destructive" as const,
        },
      ].filter(Boolean) as DropdownAction[],
    [showOptOut, handleOptOut],
  );

  return (
    <Stack className="flex-none bg-bg-secondary/50 p-6" gap="sm" fullWidth>
      <Stack direction="row" justify="between" fullWidth>
        <TextMessageAttribution
          signupSource={signupSource}
          messageBody={oldestTextMessage?.body || ""}
          isLoading={isLoading}
        />
        <Stack direction="row" align="center" gap="sm">
          {dropdownActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <EllipsisVerticalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {dropdownActions.map((action, index) => (
                  <DropdownMenuItem
                    key={index}
                    onClick={action.onClick}
                    className={
                      action.variant === "destructive"
                        ? "text-red-600 focus:text-red-600"
                        : ""
                    }
                  >
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <XMarkIcon
            className="h-6 w-6 cursor-pointer"
            onClick={() => setIsSidebarOpen(false)}
          />
        </Stack>
      </Stack>
      <Stack gap="xs">
        <Typography variant="subtitleW2">{customerToShow}</Typography>
        {customerToShow === customer?.name && (
          <Typography variant="captionW1">
            {formatPhoneNumber(customer?.phone_number)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

TextMessageSidebarHeader.displayName = "TextMessageSidebarHeader";
